export const ARENA = {
  size: 240,
  wallHeight: 6,
  wallThickness: 1.5,
  obstacleCount: 24,
} as const;

export const TICK_RATE = 30;
export const TICK_INTERVAL_MS = 1000 / TICK_RATE;
export const NETWORK_SEND_RATE = 20;

export const INITIAL_LIVES = 3;

/**
 * Lives value used by the deathmatch round profile (queue + hosted DM), where
 * elimination must be unreachable and ranking is by kills alone.
 *
 * Why a large finite number and NOT Infinity: the Colyseus schema encoder
 * serialises Infinity, and it decodes client-side as Number.MAX_SAFE_INTEGER
 * (9007199254740991), which the HUD would happily render. A finite sentinel
 * keeps the wire honest.
 *
 * Headroom: at RESPAWN_DELAY_MS (2500ms) a player can die at most ~24 times a
 * minute, so this survives a ~7h round — far beyond any configurable length.
 *
 * HUD NOTE — do NOT test for this value exactly. recordDeath() decrements lives
 * in every mode, so a DM player reads 9998, 9997, ... after each death; an
 * `=== DM_LIVES` check would render "∞" until first blood and then 9998 hearts.
 * Use a threshold instead (see client/src/ui/livesFormat.ts): no real mode
 * grants more than INITIAL_LIVES, so "> 10" cleanly separates DM from LMS and
 * also catches the MAX_SAFE_INTEGER an accidental Infinity would decode to.
 */
export const DM_LIVES = 9999;

export const CHAT_MAX_LENGTH = 120;
export const CHAT_MAX_HISTORY = 50;

export const RESPAWN_DELAY_MS = 2500;
export const PROJECTILE_LIFETIME_MS = 2200;
export const PROJECTILE_RADIUS = 0.25;
export const PLAYER_HIT_RADIUS = 1.5;

export const ROCKET_LAUNCHER_DAMAGE = 80;
export const ROCKET_LAUNCHER_SPEED = 250;
export const ROCKET_LAUNCHER_RELOAD_MS = 2500;
export const ROCKET_LAUNCHER_RANGE = 180;

export const SPAWN_POINTS: Array<{ x: number; z: number; rot: number }> = [
  { x: -88, z: -88, rot: 0.78 },
  { x:  88, z: -88, rot: -0.78 },
  { x: -88, z:  88, rot: 2.36 },
  { x:  88, z:  88, rot: -2.36 },
  { x:   0, z: -96, rot: 0 },
  { x:   0, z:  96, rot: Math.PI },
  { x: -96, z:   0, rot: Math.PI / 2 },
  { x:  96, z:   0, rot: -Math.PI / 2 },
];

export const STAT_BUDGET = {
  // 100 → 105 (Phase A2). 105 → 110: weapon range/lifetime/bounces are now
  // budget-costed (previously free power); the ceiling rises to keep existing
  // tuned content valid while the cost still bounds the max-everything exploit.
  total: 110,
  weights: {
    maxSpeed: 0.9,
    acceleration: 0.7,
    braking: 0.3,
    turnRate: 1.5,
    traction: 8,
    drift: 4,
    damage: 0.6,
    reload: 4500,
    // Set to 0: projectile speed is now a fixed archetype property, not a tuneable
    // vehicle stat. Balance between archetypes is enforced by design (DPS, TTK),
    // not by budget cost.
    projectileSpeed: 0,
    maxHealth: 0.18,
    armour: 1.0,
    mass: -1.0,
    // Combat-advantage weapon stats — previously uncosted, which let a
    // budget-legal build max range/lifetime/bounces for free. Baselined at min.
    range: 0.02,
    projectileLifetimeMs: 0.0015,
    maxBounces: 0.8,
  },
} as const;

export const LIMITS = {
  body: {
    length: { min: 2.5, max: 6.5 },
    width: { min: 1.4, max: 2.8 },
    height: { min: 0.8, max: 2.2 },
  },
  wheels: {
    size: { min: 0.3, max: 0.9 },
    width: { min: 0.18, max: 0.55 },
  },
  weapon: {
    damage: { min: 5, max: 100 },
    reloadMs: { min: 120, max: 2500 },
    // Raised to 600 in Phase A. Swept CCD handles tunnelling; budget weight is 0.
    projectileSpeed: { min: 70, max: 600 },
    // Effective travel distance in metres before the projectile expires.
    range: { min: 50, max: 500 },
    // Per-projectile lifetime; derived from range / speed but stored explicitly.
    projectileLifetimeMs: { min: 50, max: 5000 },
  },
  handling: {
    maxSpeed: { min: 12, max: 100 },
    acceleration: { min: 5, max: 40 },
    braking: { min: 6, max: 50 },
    turnRate: { min: 0.8, max: 4.5 },
    traction: { min: 0.2, max: 0.95 },
    drift: { min: 0.05, max: 0.6 },
  },
  stats: {
    maxHealth: { min: 40, max: 240 },
    armour: { min: 0, max: 12 },
    mass: { min: 4, max: 22 },
  },
} as const;
